import {
	ReactElement,
	useEffect,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	Dialog,
	FormattedText,
	Form,
	FormItem,
	TextInput,
	ValidationError,
	Toolbar,
	SecretValueInput,
	Select,
	SelectItem
} from "client/Components";
import { useFormHandler, IFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	isNotNullNorUndefined,
	isNotEmpty,
	doesNotExceedLength,
	isUrl
} from "client/Validation/rules";
import {
	MagisterConnectionSaveModel,
	create,
	update,
	get,
	MagisterConnectionListItemModel,
	MagisterApiVersion
} from "client/Api/MagisterConfiguration/magisterConfiguration";
import { SecretValue } from "client/Api/Apps/common";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { IValueInputAttachable } from "client/Forms/attachable";

export type MagisterConnectionDialogProps = {
	connectionId?: number;
	onAfterSave(item: MagisterConnectionListItemModel): void;
	onClose(): void;
};

export const MagisterConnectionDialog = (props: MagisterConnectionDialogProps): ReactElement => {
	const {
		connectionId,
		onAfterSave,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [model, setModel] = useState<MagisterConnectionSaveModel>({
		name: "",
		serverUrl: "",
		username: "",
		apiVersion: MagisterApiVersion.V1,
		password: {
			containsNewValue: true,
			value: ""
		}
	});
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<MagisterConnectionSaveModel> = useFormHandler<MagisterConnectionSaveModel>(
		model,
		setModel,
		validationConfiguration
	);

	useEffect(
		(): (() => void) | undefined => {
			if (connectionId === undefined) {
				return undefined;
			}

			const loadConnection = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const {
						name,
						serverUrl,
						username,
						apiVersion
					} = await get(
						connectionId,
						signal
					);

					setModel({
						name,
						serverUrl,
						username,
						password: {
							containsNewValue: false,
							value: null
						},
						apiVersion
					});

					setIsBusy(false);

				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			loadConnection(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			connectionId,
			onClose
		]
	);

	const isNewConnection: boolean = connectionId === undefined;

	let titleKey: LocalizationKey;
	let saveKey: LocalizationKey;
	if (isNewConnection) {
		titleKey = "_NewNameConnection";
		saveKey = "_Add";
	} else {
		titleKey = "_EditNameConnection";
		saveKey = "_Save";
	}

	const submitHandler = async (): Promise<void> => {
		if (isBusy) {
			return;
		}

		setIsBusy(true);
		const validationResult: InputValidationResult = await formHandler.validation
			.validate();

		if (validationResult.kind === InputValidationResultKind.Invalid) {
			setIsBusy(false);
			return;
		}

		let id: number | undefined = connectionId;
		try {
			if (id === undefined) {
				id = (await create(model, globalCancellation))
					.connectionId;
			} else {
				await update(
					id,
					model,
					globalCancellation
				);
			}

			onClose();

			onAfterSave({
				id,
				name: model.name,
				serverUrl: model.serverUrl
			});
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			if (!formHandler.validation.tryProcessServerError(e)) {
				alertManager.showError(localize("_ChangesNotSaved"));
			}

			setIsBusy(false);
		}
	};

	const enforceToSupplyPassword = (): void => {
		const attachable: IValueInputAttachable<SecretValue> = formHandler.for("password");

		const { containsNewValue } = attachable.value;
		if (containsNewValue) {
			return;
		}

		attachable.reducer
			.setValue({
				containsNewValue: true,
				value: ""
			});

		attachable.validation
			.triggerInputFinished();
	};

	return (
		<>
			<Dialog hasBusyIndicator={isBusy} trackingId="magister-connection-dialog">
				<Dialog.Header
					title={
						<FormattedText
							text={localize(titleKey)}
							thingsToReplace={[
								{ what: "{{Name}}", filler: localize("_Magister")}
							]}
						/>
					}
					onClose={onClose}
				/>
				<Dialog.Body>
					<Form onSubmit={submitHandler}>
						<FormItem label={localize("_Name")} isRequired>
							<TextInput {...formHandler.for("name").attachToTextInput()} />
							<ValidationError {...formHandler.for("name").attachToValidationError()} />
						</FormItem>

						<FormItem label={localize("_ServerUrl")} isRequired>
							<TextInput
								{...formHandler.for("serverUrl")
									.attachToTextInput()
									.withOnChange(enforceToSupplyPassword)
								}
							/>
							<ValidationError {...formHandler.for("serverUrl").attachToValidationError()} />
						</FormItem>

						<FormItem label={localize("_Username")} isRequired>
							<TextInput
								{...formHandler.for("username")
									.attachToTextInput()
									.withOnChange(enforceToSupplyPassword)
								}
							/>
							<ValidationError {...formHandler.for("username").attachToValidationError()} />
						</FormItem>

						<FormItem label={localize("_Password")} isRequired>
							<SecretValueInput
								{...formHandler.for("password").attachToSecretValueInput()}
								containsInitialValue={!isNewConnection}
							/>
							<ValidationError {...formHandler.for("password").attachToValidationError()} />
						</FormItem>

						<FormItem label={localize("_ApiVersion")} isRequired>
							<Select
								{...formHandler.for("apiVersion").attachToSelect()}
								options={apiVersionOptions}
								menuTitle={localize("_ApiVersion")}
							/>
							<ValidationError {...formHandler.for("apiVersion").attachToValidationError()} />
						</FormItem>
					</Form>
				</Dialog.Body>
				<Dialog.Footer>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={localize(saveKey)}
							onClick={submitHandler}
							trackingId="save"
						/>

						<Toolbar.Button
							kind="secondary"
							text={localize("_Cancel")}
							onClick={onClose}
							trackingId="cancel"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
		</>
	);
};
MagisterConnectionDialog.displayName = "MagisterConnectionDialog";

const validationConfiguration: IValidationConfiguration<MagisterConnectionSaveModel> =
	createValidationConfiguration<MagisterConnectionSaveModel>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationConfiguration.forScalar("serverUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: true,
			allowQueryString: false
		})
	);

validationConfiguration.forScalar("username")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validationConfiguration.forObject("password")
	.when((x) => x.containsNewValue)
	.forScalar("value")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

const apiVersionOptions: readonly SelectItem<MagisterApiVersion>[] = [
	{
		value: MagisterApiVersion.V1,
		text: "1.x"
	}
];
